/**
 * 언어에 맞춘 날짜·숫자 표기 — `docs/ops/app-i18n-plan.md` §17
 *
 * 문구를 `tt()` 로 다 옮겨도 **날짜가 한국식으로 굳어 있으면** 영어 화면에 `2026. 9. 7.` 이 남는다.
 * 여기서 현재 언어에 맞는 로케일 태그를 골라 준다.
 *
 * ⚠️ **표시용에만 쓴다.** `YYYY-MM-DD`·`YYYYMM` 처럼 **값으로 쓰이는** 날짜 문자열은 절대 통과시키지 말 것 —
 * 폼 입력값·DB 저장·정산월 키는 언어와 무관하게 같은 모양이어야 한다(그래서 이 파일에 ISO 헬퍼는 없다).
 */
import i18n from "../i18n";

const LOCALE_TAG = { ko: "ko-KR", en: "en-US", ja: "ja-JP", zh: "zh-CN" };

/** 현재 언어의 Intl 로케일 태그. 모르는 언어면 한국어. */
export function localeTag(lang = i18n.language) {
  return LOCALE_TAG[lang] || LOCALE_TAG.ko;
}

/**
 * 「3일 전」 같은 상대 시각. date-fns 대신 `Intl.RelativeTimeFormat` 을 쓴다 —
 * 네 언어를 한 벌로 처리하고 로케일 번들을 더 싣지 않는다(date-fns v4 는 ESM 이라 테스트에서도 걸린다).
 */
export function fmtRelative(value, now = Date.now()) {
  const d = value instanceof Date ? value : new Date(value);
  if (!value || Number.isNaN(d.getTime())) return "";
  const diffSec = Math.round((d.getTime() - now) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(localeTag(), { numeric: "auto" });
  const units = [
    ["year", 31536000], ["month", 2592000], ["week", 604800],
    ["day", 86400], ["hour", 3600], ["minute", 60],
  ];
  for (const [unit, sec] of units) {
    if (abs >= sec) return rtf.format(Math.round(diffSec / sec), unit);
  }
  return rtf.format(Math.round(diffSec), "second");
}

/** 표시용 날짜. 값이 비었거나 못 읽으면 빈 문자열 — 화면에 `Invalid Date` 를 내보내지 않는다. */
export function fmtDate(value, options = { year: "numeric", month: "long", day: "numeric" }) {
  const d = value instanceof Date ? value : new Date(value);
  if (!value || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(localeTag(), options);
}

/** 표시용 날짜+시각. */
export function fmtDateTime(value, options = { dateStyle: "medium", timeStyle: "short" }) {
  const d = value instanceof Date ? value : new Date(value);
  if (!value || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(localeTag(), options);
}

/**
 * 「9월」·「9월 7일」·「2027년 9월 1일」 같은 **월 라벨** (2026-09-07 — 전수 검수에서 잡힌 조립 문제).
 * 종전에는 `tt("{{v0}}월")` 로 옮겨 영어에서 「9」·「11/1」만 남았다(「9 Waived · 10 too Waived」). 숫자를 월 이름으로
 * 바꾸는 일은 사전이 못 하므로 여기서 Intl 로 만든다 — en 「Sep」·「Sep 7」·「Sep 1, 2027」, ja·zh 「9月」·「9月7日」·「2027年9月1日」.
 * 한국어는 종전 문자열 그대로. 값(YYYY-MM-DD)이 아니라 **표시용**이다.
 */
export function fmtMonth(month, lang = i18n.language) {
  const m = Number(month);
  if (!Number.isInteger(m) || m < 1 || m > 12) return "";
  if (!lang || lang === "ko" || !LOCALE_TAG[lang]) return `${m}월`;
  return new Date(2000, m - 1, 1).toLocaleDateString(localeTag(lang), { month: "short" });
}

export function fmtMonthDay(month, day, lang = i18n.language) {
  const m = Number(month);
  const d = Number(day);
  if (!Number.isInteger(m) || m < 1 || m > 12 || !Number.isInteger(d) || d < 1 || d > 31) return "";
  if (!lang || lang === "ko" || !LOCALE_TAG[lang]) return `${m}월 ${d}일`;
  return new Date(2000, m - 1, d).toLocaleDateString(localeTag(lang), { month: "short", day: "numeric" });
}

export function fmtYearMonthDay(year, month, day, lang = i18n.language) {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12 || !Number.isInteger(d) || d < 1 || d > 31) return "";
  if (!lang || lang === "ko" || !LOCALE_TAG[lang]) return `${y}년 ${m}월 ${d}일`;
  return new Date(y, m - 1, d).toLocaleDateString(localeTag(lang), { year: "numeric", month: "short", day: "numeric" });
}

/** 지금 언어가 한국어(만·억 단위)인지 — 축약 숫자·조사 조립처럼 한국어에만 있는 규칙을 가를 때 쓴다. */
export function isKoreanLang(lang = i18n.language) {
  return !lang || lang === "ko" || !LOCALE_TAG[lang];
}

/**
 * 큰 수의 축약 표기 — 한국어 밖에서만 쓴다(한국어는 각 호출부의 만·억 규칙 그대로).
 * en 「1.5M」·「16.4K」, ja·zh 「1.5億」·「16.4万」(Intl compact). 종전에는 `{{v0}}만` → en `{{v0}}0k` 같은 사전 치환이라
 * 소수 입력(1.5)에서 「1.50k」 가 됐다(2026-09-07 전수 검수).
 */
export function fmtCompact(value, lang = i18n.language) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat(localeTag(lang), { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

/** 표시용 숫자(천 단위 구분). 네 언어 모두 `1,234` 지만 호출부를 한곳으로 모아 둔다. */
export function fmtNum(value, options) {
  const n = Number(value);
  return (Number.isFinite(n) ? n : 0).toLocaleString(localeTag(), options);
}

export default fmtDate;
